import React, { Component } from "react";
import PromineoDataGrid from "components/common/controls/grid/PromineoDataGrid";
import { DataGridType } from "components/common/controls/grid/PromineoBaseDataGrid";
import {
  Column,
  IDataGridOptions,
  Scrolling,
} from "devextreme-react/data-grid";
import { connect } from "react-redux";
import { RootState } from "store/store";
import SubscriptionTokenResponse from "interfaces/response/SubscriptionTokenResponse";
import Status from "components/common/Status";
import ManageTokenGridActionHeader from "./ManageTokenGridActionHeader";
import { TokenStatus } from "shared/enums/TokenStatusEnum";
import { Variant } from "shared/enums/VariantEnum";
import { getFullDateFromString } from "shared/utility/DateTimeUtility";
import moment from "moment";
import ManageTokenGridActions from "./ManageTokenGridActions";
import TagControl from "components/common/TagControl";
import EditTokenPopup from "./EditTokenPopup";
import RegenerateToken from "./RegenerateToken";

interface Props {
  dataSource: SubscriptionTokenResponse[];
}

interface State {
  isEditTokenPopupVisible: boolean;
  isRegenerateTokenPopupVisible: boolean;
  slectedRowData: SubscriptionTokenResponse | null;
}

export class ManageTokenGrid extends Component<Props, State> {
  state: Readonly<State> = {
    isEditTokenPopupVisible: false,
    isRegenerateTokenPopupVisible: false,
    slectedRowData: null,
  };

  dataGridRef = React.createRef<DataGridType>();
  expirationWarningDayDuration = 30;

  handleFilterChange = ({ value }: { display: string; value: number }) => {
    if (value === -1) {
      //All
      this.dataGridRef.current?.instance.clearFilter();
    } else {
      this.dataGridRef.current?.instance.filter(["status", "=", value]);
    }
  };

  TokenNameCellRender = ({ data }: { data: SubscriptionTokenResponse }) => {
    return (
      <div>
        <span className="font-sm font-semibold">{data.name}</span>
        <span className="font-sm text-promGray2">({data.keyIdentifier})</span>
      </div>
    );
  };

  TagCellRender = ({ data }: { data: SubscriptionTokenResponse }) => {
    return <TagControl tags={data.tags} />;
  };

  StatusCellRender = ({ data }: { data: SubscriptionTokenResponse }) => {
    const getStatusVariant = () => {
      if (data.status === TokenStatus.Active) {
        return Variant.Success;
      }
      if (data.status === TokenStatus.Expired) {
        return Variant.Danger;
      }
      return Variant.Warning;
    };
    return (
      <Status
        variant={getStatusVariant()}
        text={data.statusDisplayText}
        widthClass="w-19"
      />
    );
  };

  ExpirationTimeCellRender = ({ data }: { data: SubscriptionTokenResponse }) => {
    const getRemainingDaysToExpire = () => {
      return moment(data.expirationTime).diff(moment(), "days");
    };

    const remainingDays = getRemainingDaysToExpire();

    const checkIfAboutToExpire = () => {
      return remainingDays <= this.expirationWarningDayDuration;
    };

    const getFormattedDate = () => {
      return getFullDateFromString(data.expirationTime);
    };

    const getTitleTooltip = () => {
      if (
        remainingDays < this.expirationWarningDayDuration &&
        remainingDays >= 0
      ) {
        return `About to expire in ${remainingDays} days.`;
      }
      return "Expired";
    };

    return (
      <>
        {checkIfAboutToExpire() ? (
          <div title={getTitleTooltip()}>
            <Status variant={Variant.Danger} text={getFormattedDate()}></Status>
          </div>
        ) : (
          <>{getFormattedDate()}</>
        )}
      </>
    );
  };

  handleEditTokenClick = (data: SubscriptionTokenResponse) => {
    this.setState({
      isEditTokenPopupVisible: true,
      slectedRowData: data,
    });
  };

  handleEditTokenPopupClose = () => {
    this.setState({
      isEditTokenPopupVisible: false,
    });
  };

  handleRegenerateTokenClick = (data: SubscriptionTokenResponse) => {
    this.setState({
      isRegenerateTokenPopupVisible: true,
      slectedRowData: data,
    });
  };

  handleRegenerateTokenPopupClose = () => {
    this.setState({
      isRegenerateTokenPopupVisible: false,
    });
  };

  render() {
    const dataGridOptions: IDataGridOptions = {
      dataSource: this.props.dataSource,
      keyExpr: "id",
    };

    return (
      <>
        <div className="mb-20">
          <ManageTokenGridActionHeader
            onFilterChange={this.handleFilterChange}
          />
        </div>
        <div>
          <PromineoDataGrid
            dataGridOptions={dataGridOptions}
            gridRef={this.dataGridRef}
            classNames="max-h-600px"
            noDataText="No personal access tokens
            (Click ‘New Token’ button to get started)"
          >
            <Scrolling mode="virtual" rowRenderingMode="virtual" />
            <Column
              dataField="name"
              caption="Token Name"
              cellRender={this.TokenNameCellRender}
              width="32%"
            />
            <Column
              caption="Status"
              dataField="status"
              cellRender={this.StatusCellRender}
              width="95px"
              alignment="center"
            />
            <Column
              dataField="generationTime"
              caption="Date issued"
              width="130px"
              customizeText={(data: any) =>
                getFullDateFromString(data.generationTime)
              }
              alignment="center"
              sortOrder="desc"
            />
            <Column
              dataField="expirationTime"
              caption="Expires on"
              cellRender={this.ExpirationTimeCellRender}
              width="115px"
              alignment="center"
            />
            <Column
              caption="Tags"
              cellRender={this.TagCellRender}
            />
            <Column
              caption="Actions"
              cssClass="w-20 xl:w-48"
              cellRender={(data: any) => (
                <ManageTokenGridActions
                  data={data}
                  onEditClick={this.handleEditTokenClick}
                  onRegenerateClick={this.handleRegenerateTokenClick}
                />
              )}
            />
          </PromineoDataGrid>
        </div>
        {this.state.isEditTokenPopupVisible && (
          <EditTokenPopup
            onClose={this.handleEditTokenPopupClose}
            token={this.state.slectedRowData}
          />
        )}
        {this.state.isRegenerateTokenPopupVisible && (
          <RegenerateToken
            token={this.state.slectedRowData}
            onClose={this.handleRegenerateTokenPopupClose}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: RootState, props: Props) => {
  return props;
};

export default connect(mapStateToProps)(ManageTokenGrid);
